import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "./AuthContext";
import SideBarIcon from "./components/SideBarIcon";
import Button from "react-bootstrap/Button";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";

const Divider = ({ mobile }) => (
  <div
    style={{
      width: mobile ? "calc(100% - 24px)" : "28px",
      height: "1px",
      background: "rgba(255,255,255,0.15)",
      margin: mobile ? "8px 12px" : "6px auto",
    }}
  />
);


// Props:
//   mobile  → si true, render como drawer (ancho 240px, items con nombre, slide-in)
//   open    → solo aplica en mobile; controla la visibilidad/posición
//   onItemClick → callback al click un item; en mobile se usa para cerrar drawer
// Opciones del modo "Ver como" (solo admin real). value "" = vista real admin.
const VISTA_COMO_OPCIONES = [
  { value: "",           label: "Admin (mi vista)" },
  { value: "operator",   label: "Taller" },
  { value: "seller",     label: "Ventas" },
  { value: "accountant", label: "Contable" },
];

const SideBar = ({ mobile = false, open = false, onItemClick }) => {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  // "Ver como": visible SOLO para admin real. Para operator/seller/accountant
  // reales este bloque ni existe.
  const esAdminReal = (authContext?.rolesReales || []).includes("admin");
  const vistaComo = authContext?.vistaComo || null;

  // Desktop: se expande al pasar el mouse para mostrar los nombres de cada
  // sección (los íconos solos no siempre se reconocen). En mobile no aplica
  // (el drawer ya muestra nombres).
  const [expanded, setExpanded] = useState(false);
  const showLabels = mobile || expanded;

  // El menú YA VIENE FILTRADO por el servidor (GET /user/me/descriptor, tabla
  // backend/config/roles.js). Acá no queda ninguna lista de roles: si el ítem
  // llegó, se dibuja. Mientras el descriptor no llega, no hay ítems que dibujar
  // — es el mismo instante en que la app todavía no cargó nada.
  const visibleItems = authContext?.descriptor?.menu || [];

  // Agrupar para insertar dividers solo entre grupos no vacíos.
  const groups = [];
  let currentGroup = null;
  for (const item of visibleItems) {
    if (!currentGroup || currentGroup.grupo !== item.grupo) {
      currentGroup = { grupo: item.grupo, items: [item] };
      groups.push(currentGroup);
    } else {
      currentGroup.items.push(item);
    }
  }

  // Estilos del contenedor — desktop = columna fija 60px; mobile = drawer 260px slide.
  // En mobile aplicamos safe-area-insets para iOS: el drawer cubre full screen
  // y debe respetar el notch arriba y el home indicator abajo.
  const containerStyle = mobile
    ? {
        position: "fixed",
        top: 0,
        left: 0,
        bottom: 0,
        width: 260,
        background: "linear-gradient(180deg, #1c1413 0%, #141414 100%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        paddingTop: "calc(12px + env(safe-area-inset-top))",
        paddingBottom: "calc(8px + env(safe-area-inset-bottom))",
        paddingLeft: "env(safe-area-inset-left)",
        paddingRight: 0,
        overflowY: "auto",
        borderRight: "2px solid #2a2a2a",
        zIndex: 1100,
        transform: open ? "translateX(0)" : "translateX(-100%)",
        transition: "transform .22s ease-out",
        boxShadow: open ? "2px 0 12px rgba(0,0,0,0.35)" : "none",
      }
    : {
        // Panel fijo que flota sobre el contenido; un spacer de 60px en el
        // layout (ver return) evita que el contenido se mueva al expandir.
        position: "fixed",
        top: 0,
        left: 0,
        bottom: 0,
        width: expanded ? 212 : 60,
        background: "linear-gradient(180deg, #1c1413 0%, #141414 100%)",
        display: "flex",
        flexDirection: "column",
        alignItems: expanded ? "stretch" : "center",
        padding: "8px 0",
        overflowY: "auto",
        overflowX: "hidden",
        borderRight: "2px solid #2a2a2a",
        transition: "width .18s ease-out",
        zIndex: 1100,
        boxShadow: expanded ? "2px 0 16px rgba(0,0,0,0.45)" : "none",
        whiteSpace: "nowrap",
      };

  // Logo ERP: con etiquetas (mobile o sidebar expandido) = ícono + texto;
  // compacto (desktop colapsado) = solo el ícono.
  const iconSrc = `${process.env.PUBLIC_URL}/logos/binimeliz-erp-icon.png`;
  const logo = showLabels ? (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "0 16px 10px",
        color: "#fff",
        fontWeight: 700,
        fontSize: 17,
      }}
    >
      <img src={iconSrc} alt="Binimeliz ERP" style={{ width: 44, height: 44, borderRadius: 10 }} />
      <span>Binimeliz <span style={{ color: "#e30613" }}>ERP</span></span>
    </div>
  ) : (
    <img
      src={iconSrc}
      alt="Binimeliz ERP"
      title="Binimeliz ERP"
      style={{ width: 48, height: 48, borderRadius: 10, marginBottom: 12, flexShrink: 0 }}
    />
  );

  // En mobile, el bloque de usuario es una fila al final con avatar + nombre + botón Salir.
  // En desktop, es el popover que ya teníamos.
  const userBlock = mobile ? (
    <div
      style={{
        borderTop: "1px solid rgba(255,255,255,0.15)",
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        color: "#fff",
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600 }}>
        {`${authContext.user?.firstName || ""} ${authContext.user?.lastName || ""}`.trim() || "Usuario"}
      </div>
      <Button
        variant="dark"
        size="sm"
        onClick={() => {
          if (onItemClick) onItemClick();
          authContext.logout();
        }}
      >
        Salir
      </Button>
    </div>
  ) : (
    <OverlayTrigger
      trigger="click"
      placement="top"
      overlay={
        <Popover>
          <Popover.Body>
            <div className="mb-3 fw-bold" style={{ fontSize: "16px" }}>
              {`${authContext.user?.firstName} ${authContext.user?.lastName}`}
            </div>
            <Button
              variant="primary"
              className="align-self-center"
              onClick={() => authContext.logout()}
            >
              Salir
            </Button>
          </Popover.Body>
        </Popover>
      }
    >
      <div
        style={{
          width: 44,
          height: 44,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "rgba(255,255,255,0.7)",
          fontSize: 24,
          marginBottom: 4,
        }}
      >
        <i className="bi bi-person-circle" />
      </div>
    </OverlayTrigger>
  );

  // Control "Ver como" — zona del usuario, abajo. Con etiquetas (mobile o
  // desktop expandido) es un select compacto; en desktop colapsado queda el
  // ojo como indicador (al acercar el mouse la barra se expande sola y
  // aparece el select). Al cambiar de vista se navega a "/" para que
  // RoleLanding aterrice donde corresponde al rol.
  const cambiarVista = valor => {
    authContext.setVistaComo(valor === "" ? null : valor);
    if (onItemClick) onItemClick();
    navigate("/");
  };

  const verComoBlock = !esAdminReal ? null : showLabels ? (
    <div style={{ padding: "0 12px 10px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: ".05em",
          textTransform: "uppercase",
          color: vistaComo ? "#ffb84d" : "rgba(255,255,255,0.55)",
          marginBottom: 5,
        }}
      >
        <i className="bi bi-eye" style={{ fontSize: 13 }} />
        Ver como
      </div>
      <select
        value={vistaComo || ""}
        onChange={e => cambiarVista(e.target.value)}
        style={{
          width: "100%",
          background: "#232323",
          color: vistaComo ? "#ffb84d" : "#ddd",
          border: `1px solid ${vistaComo ? "#7a4a00" : "#3a3a3a"}`,
          borderRadius: 8,
          padding: "6px 8px",
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
          outline: "none",
        }}
      >
        {VISTA_COMO_OPCIONES.map(o => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  ) : (
    <div
      title={`Ver como: ${
        VISTA_COMO_OPCIONES.find(o => o.value === (vistaComo || ""))?.label ||
        "Admin"
      }`}
      style={{
        width: 44,
        height: 32,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: vistaComo ? "#ffb84d" : "rgba(255,255,255,0.45)",
        fontSize: 17,
        marginBottom: 2,
      }}
    >
      <i className="bi bi-eye" />
    </div>
  );

  const inner = (
    <>
      {logo}

      {/* La clave lleva el índice porque "Emitir DTE" es grupo 3 y va al final:
          hay DOS bloques con grupo 3 y sólo el número los haría chocar. */}
      {groups.map((g, gi) => (
        <React.Fragment key={`${g.grupo}-${gi}`}>
          {gi > 0 && <Divider mobile={showLabels} />}
          {g.items.map((item) => (
            <SideBarIcon
              key={item.path}
              icon={item.icono}
              path={item.path}
              name={item.nombre}
              showName={showLabels}
              onClick={onItemClick}
            />
          ))}
        </React.Fragment>
      ))}

      <div style={{ flex: 1 }} />

      {verComoBlock}
      {userBlock}
    </>
  );

  if (mobile) {
    return (
      <div style={containerStyle} aria-hidden={!open}>
        {inner}
      </div>
    );
  }

  // Desktop: spacer de 60px (mantiene el layout) + panel fijo expandible al hover.
  return (
    <div style={{ width: 60, minWidth: 60, flexShrink: 0, alignSelf: "stretch" }}>
      <div
        style={containerStyle}
        onMouseEnter={() => setExpanded(true)}
        onMouseLeave={() => setExpanded(false)}
      >
        {inner}
      </div>
    </div>
  );
};

export default SideBar;
